import {
  AssignRoleItem,
  IAttributeRegistry,
  IGovernanceStructure,
  IKeyPersonEligibleEntity,
  KeyPersonRoleType,
  roleLabel,
} from '@commenda/api';
import { NonTimezoneSensitiveDate } from '@commenda/utils';
import { z } from 'zod';
import {
  type BuilderFieldType,
  customFieldDraftsToParams,
} from '../CustomFieldsBuilder';
import { UBO_CONTROL_TYPE_OPTIONS } from './constants';
import type { AddedRole, ConfigDraft, RoleOption, SharedField } from './types';

// GOVERNANCE can't be spread across a selection that doesn't offer the same options. Compare the
// options themselves: two entity types can differ and still offer the same roles.
export const applyToAllDisabledReason = (
  roleType: KeyPersonRoleType,
  entities: IKeyPersonEligibleEntity[]
): string | null => {
  if (roleType !== 'GOVERNANCE') return null;
  const optionSets = new Set(
    entities.map((e) =>
      e.allowedGovernanceRoles.length === 0
        ? null
        : [...e.allowedGovernanceRoles].sort().join('|')
    )
  );
  if (optionSets.size > 1 || optionSets.has(null)) {
    return "This role's options depend on the entity type, which differs across the selected entities. Add it to each entity separately.";
  }
  return null;
};

/**
 * Names the seat on the entity's governing organ the way its own jurisdiction names it — an Indian
 * LLP's "partner", a French SAS's "président" — instead of calling everyone a director.
 *
 * Display only. The value posted back is unchanged, and which option gets renamed is the backend's
 * call (`governanceSeatRole`), not a body-type mapping repeated here.
 */
export const withLocalOfficeholderTerm = (
  options: RoleOption[],
  structure: IGovernanceStructure | undefined,
  seatRole: string | undefined
): RoleOption[] => {
  const term = structure?.officeholderLocalTerm.trim();
  if (!term || !seatRole) return options;
  const label = term.charAt(0).toUpperCase() + term.slice(1);
  return options.map((option) =>
    option.value === seatRole ? { ...option, label } : option
  );
};

export const resolveRoleOptions = (
  registry: IAttributeRegistry | undefined,
  attributeKey: KeyPersonRoleType,
  unqualifiedKey: string,
  fallback: RoleOption[]
): RoleOption[] => {
  const property = registry?.attributes
    .find((a) => a.key === attributeKey)
    ?.properties.find((p) => p.unqualifiedKey === unqualifiedKey);
  if (!property || property.options.length === 0) return fallback;
  return property.options.map((o) => ({ value: o.value, label: o.label }));
};

const emailSchema = z.string().email();
export const isValidEmail = (value: string) =>
  emailSchema.safeParse(value).success;

// Registry custom props carry an AttributePropertyType; map it to the builder's input
// types (custom props are always created as one of these).
const BUILDER_FIELD_TYPES = ['TEXT', 'URL', 'EMAIL', 'DATE', 'PASSWORD', 'FILE'];
export const toBuilderFieldType = (type: string): BuilderFieldType =>
  BUILDER_FIELD_TYPES.includes(type) ? (type as BuilderFieldType) : 'TEXT';

// Stable per-label key for storing a shared field's value in a role's customValues.
export const toFieldKey = (label: string): string =>
  label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'field';

export const omitKey = (
  values: Record<string, unknown>,
  key: string
): Record<string, unknown> =>
  Object.fromEntries(Object.entries(values).filter(([k]) => k !== key));

export const EMPTY_DRAFT = (): ConfigDraft => ({
  // Default to today — start date is required for every role.
  startDate: NonTimezoneSensitiveDate.now().toISO8601String(),
  applyToAll: false,
  customFields: [],
  customValues: {},
});

let addedCounter = 0;
export const draftToAddedRoles = (
  draft: ConfigDraft,
  roleType: KeyPersonRoleType,
  corporationIds: number[]
): AddedRole[] =>
  corporationIds.map((corporationId) => {
    addedCounter += 1;
    return {
      id: `ar-${addedCounter}`,
      corporationId,
      roleType,
      startDate: draft.startDate,
      endDate: draft.endDate,
      officerRole: roleType === 'OFFICER' ? draft.officerRole : undefined,
      roleEmail: roleType === 'OFFICER' ? draft.roleEmail : undefined,
      governanceRole:
        roleType === 'GOVERNANCE' ? draft.governanceRole : undefined,
      controlType: roleType === 'UBO' ? draft.controlType : undefined,
      customFields: draft.customFields,
      customValues: draft.customValues,
    };
  });

export const addedRoleToItem = (
  r: AddedRole,
  sharedDefs: SharedField[]
): AssignRoleItem => {
  const sharedKeys = new Set(sharedDefs.map((f) => f.key));
  // Session-shared "save for future" fields → newCustomFields (registered once on submit,
  // then set + required-enforced per role); one-off draft fields also go via newCustomFields.
  // Every definition is sent so the workspace property registers even when this role leaves it
  // blank (optional fields); the value rides along only when present.
  const sharedParams = sharedDefs.map((f) => {
    const value = r.customValues[f.key];
    return {
      label: f.label,
      type: f.type,
      isRequired: f.isRequired,
      saveForFuture: true,
      value: value === '' ? undefined : value,
    };
  });
  const newCustomFields = [
    ...customFieldDraftsToParams(r.customFields),
    ...sharedParams,
  ];
  // What's left in customValues is an already-registered property value → customFields
  // (the engine validates required against these). Shared-field keys are excluded above.
  const customFields = Object.fromEntries(
    Object.entries(r.customValues).filter(
      ([key, value]) =>
        !sharedKeys.has(key) && value !== undefined && value !== ''
    )
  );
  return {
    roleType: r.roleType,
    corporationId: r.corporationId,
    ...(r.startDate ? { startDate: r.startDate } : {}),
    ...(r.endDate ? { endDate: r.endDate } : {}),
    ...(r.officerRole ? { officerRole: r.officerRole } : {}),
    ...(r.roleEmail ? { roleEmail: r.roleEmail } : {}),
    ...(r.governanceRole ? { governanceRole: r.governanceRole } : {}),
    ...(r.controlType ? { controlType: r.controlType } : {}),
    ...(Object.keys(customFields).length ? { customFields } : {}),
    ...(newCustomFields.length ? { newCustomFields } : {}),
  };
};

export const roleSummary = (
  r: AddedRole,
  registry: IAttributeRegistry | undefined
): string => {
  if (r.roleType === 'OFFICER')
    return r.officerRole ?? roleLabel(registry, 'OFFICER');
  if (r.roleType === 'GOVERNANCE')
    return r.governanceRole ?? roleLabel(registry, 'GOVERNANCE');
  if (r.roleType === 'UBO') {
    if (!r.controlType) return roleLabel(registry, 'UBO');
    const controlTypeOptions = resolveRoleOptions(
      registry,
      'UBO',
      'controlType',
      UBO_CONTROL_TYPE_OPTIONS
    );
    return (
      controlTypeOptions.find((option) => option.value === r.controlType)
        ?.label ?? roleLabel(registry, 'UBO')
    );
  }
  return roleLabel(registry, r.roleType);
};
